import React, { useEffect, useState } from 'react';
import { usersAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Plus, X, Pencil, Users, Mail, Phone, AtSign, Briefcase } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/* ── Role config (bordered pills) ── */
const ROLE_CONFIG = {
  admin:      { label: 'Admin',      cls: 'border-red-400    text-red-600    bg-red-50'    },
  legal_team: { label: 'Legal Team', cls: 'border-blue-400   text-blue-700   bg-blue-50'   },
  advocate:   { label: 'Advocate',   cls: 'border-orange-400 text-orange-600 bg-orange-50' },
  viewer:     { label: 'Viewer',     cls: 'border-teal-400   text-teal-700   bg-teal-50'   },
};

const inputCls = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white";

const emptyForm = {
  username: '', email: '', first_name: '', last_name: '',
  password: '', role: 'legal_team', phone: '', designation: ''
};

/* ── Avatar circle ── */
function Avatar({ user: u, size = 'md' }) {
  const initials = `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`.toUpperCase();
  const sz = size === 'lg'
    ? 'w-16 h-16 text-xl'
    : size === 'sm'
    ? 'w-8 h-8 text-xs'
    : 'w-10 h-10 text-sm';
  return (
    <div
      className={`${sz} rounded-full flex items-center justify-center text-white font-bold shrink-0`}
      style={{ backgroundColor: '#1a2744' }}
    >
      {initials}
    </div>
  );
}

/* ── Role Badge ── */
function RoleBadge({ role }) {
  const cfg = ROLE_CONFIG[role] || { label: role, cls: 'border-gray-300 text-gray-600 bg-gray-50' };
  return (
    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

/* ── Status Badge ── */
function StatusBadge({ active }) {
  return (
    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${
      active
        ? 'border-green-400 text-green-600 bg-green-50'
        : 'border-red-300 text-red-500 bg-red-50'
    }`}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

/* ── User Card ── */
function UserCard({ u, onEdit }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all overflow-hidden">
      {/* Card Top — navy strip with avatar + name */}
      <div className="px-5 pt-5 pb-4 flex items-center gap-4">
        <Avatar user={u} size="lg" />
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-gray-900 text-base leading-tight truncate">{u.full_name}</h3>
          {u.designation
            ? <p className="text-xs text-gray-500 mt-0.5 truncate flex items-center gap-1">
                <Briefcase size={11} className="text-gray-400 shrink-0" />
                {u.designation}
              </p>
            : <p className="text-xs text-gray-300 mt-0.5">No designation</p>
          }
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <RoleBadge role={u.role} />
            <StatusBadge active={u.is_active} />
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-50 mx-5" />

      {/* Card Body — contact info */}
      <div className="px-5 py-3 space-y-2">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <AtSign size={12} className="text-gray-400 shrink-0" />
          <span className="font-mono truncate">{u.username}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Mail size={12} className="text-gray-400 shrink-0" />
          <span className="truncate">{u.email}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Phone size={12} className="text-gray-400 shrink-0" />
          <span>{u.phone || <span className="text-gray-300">—</span>}</span>
        </div>
      </div>

      {/* Card Footer — edit button */}
      <div className="px-5 pb-4 pt-2">
        <button
          onClick={() => onEdit(u)}
          className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 py-2 rounded-lg transition-colors"
        >
          <Pencil size={12} /> Edit User
        </button>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(emptyForm);
  const [saving, setSaving]     = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const load = () => {
    usersAPI.list().then(r => setUsers(r.data.results || r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const set     = (k, v) => setForm(f     => ({ ...f, [k]: v }));
  const setEdit = (k, v) => setEditForm(f => ({ ...f, [k]: v }));

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      first_name:  u.first_name,
      last_name:   u.last_name,
      email:       u.email,
      phone:       u.phone || '',
      role:        u.role,
      designation: u.designation || '',
      is_active:   u.is_active,
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await usersAPI.create(form);
      toast.success('User created successfully!');
      setShowForm(false); setForm(emptyForm); load();
    } catch (err) {
      const d = err.response?.data;
      if (d?.username) toast.error('Username already exists');
      else toast.error('Failed to create user');
    }
    setSaving(false);
  };

  const handleEdit = async (e) => {
    e.preventDefault(); setEditSaving(true);
    try {
      await usersAPI.update(editUser.id, editForm);
      toast.success('User updated successfully!');
      setEditUser(null); load();
    } catch { toast.error('Failed to update user'); }
    setEditSaving(false);
  };

  if (user?.role !== 'admin') {
    return <div className="text-center py-20 text-gray-400">Access denied</div>;
  }

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Users</h1>
          <p className="text-sm text-gray-400">{users.length} team members</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors hover:opacity-90"
          style={{ backgroundColor: '#1a2744' }}
        >
          {showForm ? <><X size={15} /> Cancel</> : <><Plus size={15} /> Add User</>}
        </button>
      </div>

      {/* ── Create Form ── */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 space-y-4">
          <h2 className="font-semibold text-gray-800 border-b pb-2">Create New User</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">First Name *</label>
              <input className={inputCls} value={form.first_name} onChange={e => set('first_name', e.target.value)} required />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Last Name *</label>
              <input className={inputCls} value={form.last_name} onChange={e => set('last_name', e.target.value)} required />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Username *</label>
              <input className={inputCls} value={form.username} onChange={e => set('username', e.target.value)} required />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Email *</label>
              <input type="email" className={inputCls} value={form.email} onChange={e => set('email', e.target.value)} required />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Password *</label>
              <input type="password" className={inputCls} value={form.password} onChange={e => set('password', e.target.value)} required minLength={8} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Phone</label>
              <input className={inputCls} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 XXXXX XXXXX" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Role *</label>
              <select className={inputCls} value={form.role} onChange={e => set('role', e.target.value)}>
                <option value="admin">Admin — Full access</option>
                <option value="legal_team">Legal Team — Add / Edit / Upload</option>
                <option value="advocate">Advocate — Limited updates</option>
                <option value="viewer">Viewer — Read only</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Designation</label>
              <input className={inputCls} value={form.designation} onChange={e => set('designation', e.target.value)} placeholder="e.g. Senior Advocate" />
            </div>
          </div>
          <button type="submit" disabled={saving}
            className="text-white px-6 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60 hover:opacity-90 transition-colors"
            style={{ backgroundColor: '#1a2744' }}>
            {saving ? 'Creating...' : 'Create User'}
          </button>
        </form>
      )}

      {/* ── User Cards Grid ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400 gap-2 text-sm">
          <Users size={18} className="opacity-40" /> Loading users...
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Users size={44} className="mx-auto mb-3 opacity-25" />
          <p className="text-sm">No users found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {users.map(u => (
            <UserCard key={u.id} u={u} onEdit={openEdit} />
          ))}
        </div>
      )}

      {/* ── Edit Modal ── */}
      {editUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">

            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100"
              style={{ backgroundColor: '#1a2744' }}>
              <div className="flex items-center gap-3">
                <Avatar user={editUser} size="sm" />
                <div>
                  <h2 className="text-sm font-bold text-white">{editUser.full_name}</h2>
                  <p className="text-xs text-blue-300">@{editUser.username}</p>
                </div>
              </div>
              <button onClick={() => setEditUser(null)} className="text-white/60 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleEdit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">First Name *</label>
                  <input className={inputCls} value={editForm.first_name} onChange={e => setEdit('first_name', e.target.value)} required />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Last Name *</label>
                  <input className={inputCls} value={editForm.last_name} onChange={e => setEdit('last_name', e.target.value)} required />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Email *</label>
                  <input type="email" className={inputCls} value={editForm.email} onChange={e => setEdit('email', e.target.value)} required />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Phone</label>
                  <input className={inputCls} value={editForm.phone} onChange={e => setEdit('phone', e.target.value)} placeholder="+91 XXXXX XXXXX" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Role *</label>
                  <select className={inputCls} value={editForm.role} onChange={e => setEdit('role', e.target.value)}>
                    <option value="admin">Admin — Full access</option>
                    <option value="legal_team">Legal Team — Add / Edit / Upload</option>
                    <option value="advocate">Advocate — Limited updates</option>
                    <option value="viewer">Viewer — Read only</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Designation</label>
                  <input className={inputCls} value={editForm.designation} onChange={e => setEdit('designation', e.target.value)} placeholder="e.g. Senior Advocate" />
                </div>
              </div>

              {/* Active toggle */}
              <div className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: '#f8fafc' }}>
                <label className="text-sm font-medium text-gray-700 flex-1">Account Status</label>
                <button
                  type="button"
                  onClick={() => setEdit('is_active', !editForm.is_active)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editForm.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${editForm.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className={`text-xs font-semibold ${editForm.is_active ? 'text-green-600' : 'text-red-500'}`}>
                  {editForm.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="flex gap-3 pt-1">
                <button type="submit" disabled={editSaving}
                  className="flex-1 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60 hover:opacity-90 transition-colors"
                  style={{ backgroundColor: '#1a2744' }}>
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button type="button" onClick={() => setEditUser(null)}
                  className="px-5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
